<div #baseContainer class="relative pt-[64px]" id="rating-form">
	@if (!inPreviewSummary) {
		<!-- Sticky header -->
		<div class="fixed top-[64px] z-10 bg-white p-6" [style.width.px]="baseContainerWidth">
			<div class="relative flex w-full items-center justify-between">
				<h2 class="!text-[16px] !font-bold !leading-6" id="rating-title-id">Cabin Crew Form (Rating)</h2>

				<!-- Stepper quick nav -->
				<div class="custom-stepper">
					<button
						(click)="stepper.selectedIndex = 0"
						[class.completed]="stepper.selectedIndex > 0"
						[class.active]="stepper.selectedIndex === 0"
					>
						Training Information
					</button>
					<span class="aa-mat-icon">chevron_right</span>
					<button
						(click)="stepper.selectedIndex = 1"
						[class.completed]="stepper.selectedIndex > 1"
						[class.active]="stepper.selectedIndex === 1"
						[disabled]="stepper.selectedIndex < 1"
					>
						Candidate Rating
					</button>
					<span class="aa-mat-icon">chevron_right</span>
					<button
						(click)="stepper.selectedIndex = 2"
						[class.completed]="stepper.selectedIndex > 2"
						[class.active]="stepper.selectedIndex === 2"
						[disabled]="stepper.selectedIndex < 2"
					>
						Final Rating &amp; Signoff
					</button>
				</div>
			</div>
		</div>

		<mat-stepper [linear]="true" #stepper (selectionChange)="onStepperSelectionChanged($event.selectedIndex)">
			<!-- STEP 1: Training Info -->
			<mat-step [stepControl]="cabinCrewTrainingInfoForm">
				@if (cabinCrewTrainingInfoForm) {
					<form [formGroup]="cabinCrewTrainingInfoForm" class="space-y-6">
						<div class="grid grid-cols-2 gap-4 gap-y-0">
							<!-- Form Type -->
							<app-cbta-autocomplete
								label="Form Type"
								[data]="formType.value"
								placeholder="Form Type"
								[options]="sharedService.dropDownFormTypeOptions"
								(focusOut)="saveFormType($event)"
								[field]="formType"
								[isDisabled]="disableForm"
							></app-cbta-autocomplete>

							<!-- Form Name -->
							<div class="form-element">
								<mat-label for="form-name">Form Name</mat-label>
								<mat-form-field appearance="outline" class="form-field-responsive mt-1 w-full">
									<input type="text" formControlName="formName" matInput placeholder="Training Name" />
									@if (formName.errors) {
										<mat-error>{{ sharedService.getErrorMessage(formName) }}</mat-error>
									}
								</mat-form-field>
							</div>

							<!-- Test Date (Start) -->
							<app-cbta-datepicker
								[inputDesign]="'formType'"
								label="Test Date"
								placeHolder="DD MMM YYYY"
								[format]="DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT"
								[defaultValue]="trainingStartDate"
								[isDisabled]="false"
								(selectedDateChange)="onStartDateChange($event)"
							></app-cbta-datepicker>

							<!-- Syllabus End Date (kept for model binding; hidden visually if you want) -->
							<app-cbta-datepicker
								class="hidden"
								[inputDesign]="'formType'"
								label="Syllabus End Date"
								placeHolder="DD MMM YYYY"
								[format]="DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT"
								[defaultValue]="trainingEndDate"
								[minDate]="trainingEndDate"
								[isDisabled]="disableForm"
							></app-cbta-datepicker>
						</div>

						<!-- Candidates -->
						<ng-container formArrayName="candidates">
							@if (candidates.controls.length > 0) {
								<mat-accordion>
									@for (candidateCtrl of candidates.controls; track $index; let i = $index) {
										<mat-expansion-panel
											(opened)="panelOpenState = true"
											(closed)="panelOpenState = false"
											class="mat-elevation-z1"
											[expanded]="true"
										>
											<mat-expansion-panel-header class="bg-blue-20 text-black lg:bg-violet-light lg:text-violet">
												<mat-panel-title>
													<span class="font-bold leading-6 text-violet">
														{{ i === 0 ? "Candidate 1" : i === 1 ? "Candidate 2" : "Candidate " + (i + 1) }}
													</span>
												</mat-panel-title>
											</mat-expansion-panel-header>

											<app-candidate-details-group-session-v2
												[excludeLicense]="true"
												[candidateForm]="candidates.at(i)"
												[sessions]="sessionConfigs"
											></app-candidate-details-group-session-v2>
										</mat-expansion-panel>
									}
								</mat-accordion>

								<div class="relative my-6">
									<!-- line behind -->
									<div class="absolute inset-0 flex items-center" aria-hidden="true">
										<div class="w-full border-t border-gray-300"></div>
									</div>

									<!-- button on top -->
									<div class="relative flex w-full justify-center gap-2">
										@if (candidates.controls.length < MAX_CANDIDATES) {
											<button
												type="button"
												(click)="addNewCandidate()"
												class="z-10 flex h-[35px] min-w-[226px] items-center justify-center gap-1 rounded bg-green px-4 text-sm text-white"
												[disabled]="!canAddCandidate()"
												[matTooltip]="!canAddCandidate() ? 'You can only add up to 4 candidates' : ''"
											>
												<span class="aa-mat-icon">add_box</span>
												<span>Add candidate</span>
											</button>
										}
										@if (candidates.controls.length >= 2 && candidates.controls.length <= MAX_CANDIDATES) {
											<button
												type="button"
												(click)="removeCandidate(candidates.controls.length - 1)"
												class="z-10 flex h-[35px] min-w-[226px] items-center justify-center gap-1 rounded bg-[#FF5151] px-4 text-sm text-white"
												[disabled]="candidates.controls.length < 2"
											>
												<span class="aa-mat-icon">indeterminate_check_box</span>
												<span>Remove candidate</span>
											</button>
										}
									</div>
								</div>
							}
						</ng-container>

						<!-- Actions -->
						<div class="rating-form-step-actions flex flex-row justify-between gap-4">
							<button type="button" data-variant="tertiary" class="aa-btn" (click)="goback()" [disabled]="!isOnline">
								Cancel
							</button>
							<button
								type="button"
								[disabled]="cabinCrewTrainingInfoForm.invalid"
								data-variant="tertiary"
								class="aa-btn"
								(click)="saveAsTemp(1)"
							>
								Save As Draft
							</button>
							<div class="flex-1"></div>
							<button
								type="button"
								[disabled]="cabinCrewTrainingInfoForm.invalid"
								data-variant="primary"
								class="aa-btn"
								(click)="onStepperNext(stepper)"
							>
								Start Rating
							</button>
						</div>
					</form>
				}
			</mat-step>

			<!-- STEP 2: Rating -->
			<mat-step [stepControl]="cabinCrewRatingForm" class="relative">
				@if (cabinCrewRatingForm) {
					<form [formGroup]="cabinCrewRatingForm" (ngSubmit)="onRatingFormSubmit()">
						<a
							(click)="scrollToTop()"
							class="fixed bottom-28 right-10 z-40 h-10 w-10 cursor-pointer rounded-full bg-violet text-center text-violet-light lg:bottom-20"
							[matTooltip]="'Scroll to top'"
							[matTooltipPosition]="'above'"
						>
							<mat-icon class="leading-10">keyboard_arrow_up</mat-icon>
						</a>

						<div class="pt-[52px]">
							<div class="fixed top-[152px] z-10 h-[52px] bg-white px-6 pb-6" [style.width.px]="baseContainerWidth">
								<div class="flex flex-row items-center gap-2 lg:gap-4">
									<button
										type="button"
										class="button-nav flex h-[24px] min-w-6 items-center justify-center border border-teal bg-teal text-white"
										(click)="onPrev()"
										[disabled]="current === 0"
									>
										<mat-icon class="mt-1">chevron_left</mat-icon>
									</button>
									<div class="text-base font-medium leading-6 text-violet lg:text-lg">
										{{ candidateList[current] }}
									</div>
									<button
										type="button"
										(click)="onNext()"
										class="button-nav flex h-[24px] min-w-6 items-center justify-center border border-teal bg-teal text-white"
										[disabled]="current === candidateList.length - 1"
									>
										<mat-icon class="mt-1">chevron_right</mat-icon>
									</button>
								</div>
							</div>

							<div class="slide-wrapper">
								<ng-container formArrayName="ratingForm">
									@if (ratingForm.controls.length > 0) {
										@for (rf of ratingForm.controls; track $index; let i = $index) {
											<app-cbta-form-navigator [isIn]="current === i" [left]="isLeftTransition(i)">
												<app-candidate-rating-fixed-maneuver-v2
													[rfForm]="ratingForm.at(i)"
													[loaded]="ratingFormLoaded"
													[showTem]="true"
												></app-candidate-rating-fixed-maneuver-v2>
											</app-cbta-form-navigator>
										}
									}
								</ng-container>
							</div>
						</div>

						<div class="rating-form-step-actions flex flex-row justify-between gap-4">
							<button type="button" data-variant="tertiary" class="aa-btn" (click)="onStepBack(stepper)">Back</button>

							<div class="flex-1"></div>
							<button
								type="button"
								[disabled]="cabinCrewRatingForm.invalid"
								(click)="onStepperNextSignOff(stepper)"
								data-variant="primary"
								class="step-submit aa-btn"
							>
								Sign Off
							</button>
						</div>
					</form>
				}
			</mat-step>

			<!-- STEP 3: Sign Off -->
			<mat-step [stepControl]="cabinCrewSignOffForm" [editable]="true">
				@if (cabinCrewSignOffForm) {
					<form [formGroup]="cabinCrewSignOffForm" (ngSubmit)="onRatingFormSubmit()">
						<div class="relative pt-[52px]">
							<div
								class="fixed top-[152px] z-10 flex items-center justify-between bg-white px-6 pb-6 shadow-sm"
								[style.width.px]="baseContainerWidth"
							>
								<div class="flex flex-1 flex-row items-center gap-4">
									<button
										type="button"
										class="button-nav flex h-[24px] min-w-6 items-center justify-center border border-teal bg-teal text-white"
										(click)="onPrev()"
										[disabled]="current === 0"
									>
										<mat-icon class="mt-1">chevron_left</mat-icon>
									</button>
									<div class="text-base font-medium leading-6 text-violet lg:text-lg">
										{{ candidateList[current] }}
									</div>
									<button
										type="button"
										(click)="onNext()"
										class="button-nav flex h-[24px] min-w-6 items-center justify-center border border-teal bg-teal text-white"
										[disabled]="current === candidateList.length - 1"
									>
										<mat-icon class="mt-1">chevron_right</mat-icon>
									</button>
								</div>
								<button
									class="aa-btn !bg-violet"
									data-size="small"
									data-variant="primary"
									(click)="togglePreviewSummary()"
								>
									Preview Summary
								</button>
							</div>

							<div class="slide-wrapper">
								<ng-container formArrayName="signOffForm">
									@if (signOffForm.controls.length > 0) {
										@for (sf of signOffForm.controls; track $index; let i = $index) {
											<app-cbta-form-navigator [isIn]="current === i" [left]="isLeftTransition(i)">
												<app-candidate-signoff-grading
													[finalOnly]="true"
													[excludeLicense]="true"
													[index]="i"
													[signoffForm]="signOffForm.at(i)"
													[hideTraineeSignature]="isRemoteSignoffShell"
												></app-candidate-signoff-grading>
											</app-cbta-form-navigator>
										}
									}
								</ng-container>
							</div>
						</div>

						<div class="rating-form-step-actions flex flex-row justify-between gap-4">
							<button type="button" class="aa-btn" data-variant="tertiary" (click)="onStepBack(stepper)">Back</button>

							<div class="flex-1"></div>
							@if (isRemoteSignoffShell) {
								<app-remote-signoff-shell
									part="send"
									[canSend]="canSendToCandidate"
									[sending]="isSendingToCandidate"
									(send)="onSendToCandidate(stepper)"
								></app-remote-signoff-shell>
							} @else {
								<button
									type="button"
									(click)="confirmAndComplete(stepper)"
									class="step-submit aa-btn"
									data-variant="primary"
									[disabled]="signOffForm.invalid || !hasSignatures"
								>
									Submit
								</button>
							}
						</div>

						@if (signOffSuccess) {
							<div class="mt-4 bg-green-light p-3 text-green">Sign off completed successfully!</div>
						}
					</form>
				}
			</mat-step>
		</mat-stepper>
	}

	@if (inPreviewSummary) {
		<app-preview-summary-v2
			formType="cabin_crew"
			(onFinished)="onPreviewFinished($event)"
			[candidates]="previewCandidates"
		></app-preview-summary-v2>
	}

	<app-remote-signoff-shell part="overlay" [sending]="isSendingToCandidate"></app-remote-signoff-shell>
</div>
